import {
  BRAND_CENTER_PATH,
  BRAND_STRATEGY_PATH,
  CONTENT_LIBRARY_PATH,
} from 'components/brand/constants';
import { PRODUCT_CATALOG_PATH } from 'components/brand/product/constants';
import { MessageDetails } from './types';

// Navigation targets for the Message Inputs rows. Each handler routes to the
// Brand Center area where that input is configured. Spec 2026-06-11 §3.1.
export interface MessageDetailsNavigation {
  tactic: () => void;
  content: () => void;
  product: (productId?: string | null) => void;
  urgencyLevers: () => void;
  toneOfVoice: () => void;
}

// New tab so the merchant keeps their place in the conversation.
const openInNewTab = (path: string) => {
  window.open(path, '_blank', 'noopener');
};

// First content entry with a resolved URL — the same value the Content row
// displays. Only http(s) URLs are honored so a malformed snapshot can't
// smuggle in a javascript: target.
export const getContentUrl = (details: MessageDetails): string | null => {
  const entry = details.runtime_context?.content?.find(
    (item) => item.url && /^https?:\/\//i.test(item.url),
  );
  return entry?.url ?? null;
};

export const buildMessageDetailsNavigation = (
  details: MessageDetails,
): MessageDetailsNavigation => ({
  tactic: () =>
    openInNewTab(
      details.parent_id
        ? `${BRAND_STRATEGY_PATH}?tactic=${encodeURIComponent(
            details.parent_id,
          )}`
        : BRAND_STRATEGY_PATH,
    ),
  // The row displays the resolved URL, so clicking opens that URL. The
  // Content Library is only the fallback when no URL survived validation.
  content: () =>
    openInNewTab(
      getContentUrl(details) ?? `${CONTENT_LIBRARY_PATH}?resource_type=links`,
    ),
  // Catalog product ids are numeric Shopify ids; anything else (null,
  // malformed snapshot value) lands on the unfiltered catalog.
  product: (productId?: string | null) =>
    openInNewTab(
      productId && /^\d+$/.test(productId)
        ? `${PRODUCT_CATALOG_PATH}?product_id=${productId}`
        : PRODUCT_CATALOG_PATH,
    ),
  // Urgency levers are configured under the Brand Guidelines urgency
  // advanced settings.
  urgencyLevers: () => openInNewTab(`${BRAND_CENTER_PATH}#urgency`),
  toneOfVoice: () => openInNewTab(`${BRAND_CENTER_PATH}#tone`),
});
